import { Button, InputNumber, Select, message } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ErrorPopup from "./ErrorPopup";

import { hasEditPermission } from "../../helpers/rabc-helper";
import {
  getLimits,
  startStatusConfigLoader,
  updateLimits,
  resetStatusConfigState,
} from "../../redux/data360Live_StatusConfiguration/action";
import Data360LiveStatusConfigurationGrid from "../AgGrid/Data360Live-status-configuration";
import CommonLoader from "../Loaders/loader";

const { Option } = Select;

const PASS_GROUP_ORDER = [
  "root",
  "hot",
  "hot pass",
  "fill",
  "last fill",
  "strip",
  "cap",
];

const getUniqueJobNumbers = (data, weldDetails) => {
  if (!data || data.length === 0) return [];

  return [
    ...new Set(
      data.map((item) => `${item.job_number}:${item.pipe_name ?? ""}`)
    ),
  ]
    .filter((jobNumber) => jobNumber !== null && jobNumber.split(":")[0])
    .map((job_number) => {
      const [jobNum, pipeName = ""] = job_number.split(":");

      // Try to find welding_system from weldDetails first, then fallback to tableLimitData
      let welding_system = "";
      const weldDetail = weldDetails?.find((wld) => wld.job_number == jobNum);
      if (weldDetail?.welding_system) {
        welding_system = weldDetail.welding_system.replace(/\W/g, "");
      } else {
        // Fallback: get welding_system from tableLimitData
        const tableItem = data.find(
          (item) =>
            item.job_number === jobNum &&
            (pipeName
              ? item.pipe_name === pipeName
              : item.pipe_name == null || item.pipe_name === "null")
        );
        welding_system = tableItem?.welding_system?.replace(/\W/g, "") || "";
      }

      return {
        label: `${welding_system}: ${jobNum}${pipeName ? `: ${pipeName}` : ""}`,
        value: job_number,
      };
    });
};

const useStatusConfiguration = () => {
  const dispatch = useDispatch();
  const StatusData = useSelector(
    (state) => state.data360LiveStatusConfigReducer,
    (prev, next) => prev.loading === next.loading && prev.limits === next.limits
  );
  const projectReducer = useSelector(
    (state) => state.data360LiveProjectReducer,
    (prev, next) => prev.currentProject === next.currentProject
  );

  const { limits } = StatusData;
  const { allDataSet = [] } = limits || [];
  const tableLimitData = useMemo(() => [...allDataSet] || [], [allDataSet]);
  const weldDetails = useMemo(
    () => limits?.weldDetails || [],
    [limits?.weldDetails]
  );

  const selectedProject = projectReducer.currentProject || {};
  const { project_id: selectedProjectId, project_name: selectedProjectName } =
    selectedProject;

  const [selectedJobNumber, setSelectedJobNumber] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [precalculatedJobData, setPrecalculatedJobData] = useState({});

  // Memoize unique job numbers
  const uniqueJobNumbers = useMemo(
    () => getUniqueJobNumbers(tableLimitData, weldDetails),
    [tableLimitData, weldDetails]
  );

  // Memoize the precalculation logic
  const calculatePrecalculatedJobData = useCallback(() => {
    if (tableLimitData?.length === 0) return {};

    const jobDataMap = {};

    // Parse report columns once
    const { units = {} } = limits?.projectData;
    const { conversionFactors = {} } = limits;

    uniqueJobNumbers.forEach((jobNumberObj) => {
      const [jobNum, pipeName] = jobNumberObj.value.split(":");

      // Find the corresponding weld detail
      const selectedWeldDetail = weldDetails?.find(
        (ite) =>
          ite.job_number === jobNum &&
          (pipeName
            ? ite.pipe_name == pipeName
            : ite.pipe_name == null || ite.pipe_name == "null")
      );

      // Filter table limit data for this job number
      const filteredTableLimitData = tableLimitData.filter(
        (item) =>
          item.job_number === jobNum &&
          (pipeName
            ? item.pipe_name == pipeName
            : item.pipe_name == null || item.pipe_name == "null")
      );

      // Precalculate record list
      const recordList = processRecordList(
        filteredTableLimitData,
        jobNumberObj.value,
        selectedWeldDetail
      );

      // Precalculate rows and columns
      const transposedData = [
        {
          key:
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_current"
              : "current",
          label: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "Zone "
              : ""
          }Current (${units?.current || "A"})`,
          min: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }current_min`,
          max: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }current_max`,
        },
        {
          key:
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_volts"
              : "volts",
          label: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "Zone "
              : ""
          }Volts (${units?.volts || "V"})`,
          min: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }volts_min`,
          max: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }volts_max`,
        },
        {
          key:
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_wire_speed"
              : "wire_speed",
          label: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "Zone "
              : ""
          }Wire Speed (${units?.["wire_speed"] || "in/min"})`,
          min: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }wire_speed_min`,
          max: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }wire_speed_max`,
        },
        {
          key:
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_travel_speed"
              : "travel_speed",
          label: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "Zone "
              : ""
          }Travel Speed (${units?.["travel_speed"] || "in/min"})`,
          min: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }travel_speed_min`,
          max: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }travel_speed_max`,
        },
        {
          key:
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_oscillation"
              : "oscillation",
          label: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "Zone "
              : ""
          }Oscillation Width (${units?.["oscillation_width"] || "in"})`,
          min: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }oscillation_width_min`,
          max: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }oscillation_width_max`,
        },
        {
          key:
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_calculated_heat"
              : "heat",
          label: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "Zone "
              : ""
          }Heat (${units?.heat || "KJ/in"})`,
          min: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }heat_min`,
          max: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }heat_max`,
        },
        {
          key:
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_true_energy"
              : "true_energy",
          label: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "Zone "
              : ""
          }True Energy (${units?.["true_energy"] || "KJ/in"})`,
          min: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }true_energy_min`,
          max: `${
            selectedWeldDetail?.statusCalculationLevel === "Zone Level"
              ? "zone_"
              : ""
          }true_energy_max`,
        },
      ];

      const getValue = (key, originalPassName) => {
        if (!filteredTableLimitData || filteredTableLimitData.length === 0)
          return null;

        const parameter = key.replace(/_(min|max)$/, "");

        const allKeyValues = filteredTableLimitData
          .filter((ite) => ite.pass_group_name === originalPassName)
          .filter(
            (item) =>
              item[key] !== undefined && item[key] !== null && item[key] !== ""
          )
          .map((item) => Number(item[key]));

        if (allKeyValues.length > 0) {
          return {
            min: (
              (conversionFactors[parameter] || 1) * Math.min(...allKeyValues)
            ).toFixed(2),
            max: (
              (conversionFactors[parameter] || 1) * Math.max(...allKeyValues)
            ).toFixed(2),
          };
        }

        return null;
      };

      const rowsStatusConfigurationAgGrid = transposedData.reduce(
        (acc, ite) => {
          const reducedData = recordList.reduce((accInternal, record) => {
            const values = getValue(
              ite[record.key]?.toString(),
              record.original_pass_name
            );
            accInternal[record.pass_group_name] = values
              ? record.key === "min"
                ? values.min
                : values.max
              : null;
            return accInternal;
          }, {});

          acc.push({
            parameter: ite.label,
            ...reducedData,
          });

          return acc;
        },
        []
      );

      const columnsAgGrid = recordList.map((ite) => ({
        columnName: ite.pass_group_name,
        value: ite.pass_group_name,
      }));

      // Store precalculated data
      jobDataMap[jobNumberObj.value] = {
        rows: rowsStatusConfigurationAgGrid,
        columns: columnsAgGrid,
        weldDetail: selectedWeldDetail,
        limits: { conversionFactors },
      };
    });

    return jobDataMap;
  }, [tableLimitData, weldDetails, projectReducer, uniqueJobNumbers]);

  // Use useEffect to update precalculated job data
  useEffect(() => {
    const jobDataMap = calculatePrecalculatedJobData();
    setPrecalculatedJobData(jobDataMap);

    // Set initial job number if not set
    if (!selectedJobNumber && uniqueJobNumbers.length > 0) {
      setSelectedJobNumber(uniqueJobNumbers[0].value);
    }
  }, [calculatePrecalculatedJobData, selectedJobNumber, uniqueJobNumbers]);

  // Project selection effect
  useEffect(() => {
    if (selectedProjectId !== currentProjectId) {
      setSelectedJobNumber(null);
      setCurrentProjectId(selectedProjectId);
    }

    if (selectedProjectId && selectedProjectName) {
      const params = {
        skip: 0,
        limit: 100,
        project_number: selectedProjectId,
        // project_name: selectedProjectName,
      };
      dispatch(startStatusConfigLoader());
      dispatch(getLimits(params));
    }
  }, [dispatch, selectedProjectId, selectedProjectName, currentProjectId]);

  return {
    tableLimitData,
    weldDetails,
    selectedJobNumber,
    setSelectedJobNumber,
    selectedProject,
    precalculatedJobData,
  };
};

const processRecordList = (
  tableLimitData,
  selectedJobNumber,
  selectedWeldDetail
) => {
  if (!tableLimitData || !selectedJobNumber) return [];

  const [jobNum, pipeName] = selectedJobNumber.split(":");

  const filteredData = tableLimitData.filter(
    (item) =>
      item.job_number === jobNum &&
      (pipeName
        ? item.pipe_name == pipeName
        : item.pipe_name == null || item.pipe_name == "null")
  );

  const transformedData = filteredData.reduce((acc, pass) => {
    const existingPass = acc.find(
      (p) => p.pass_group_name === pass.pass_group_name
    );

    if (existingPass) {
      existingPass.values.push({ ...pass });
    } else {
      acc.push({
        pass_group_name:
          selectedWeldDetail?.statusCalculationLevel === "Zone Level"
            ? `${pass.pass_name}-${pass.zone_name}`
            : pass.pass_group_name,
        values: [{ ...pass }],
      });
    }
    return acc;
  }, []);

  const sortedData = transformedData.sort((a, b) => {
    const indexA = PASS_GROUP_ORDER.indexOf(a.pass_group_name?.toLowerCase());
    const indexB = PASS_GROUP_ORDER.indexOf(b.pass_group_name?.toLowerCase());

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  const filteredSortedData = sortedData.filter(
    (item) =>
      Boolean(item.pass_group_name) &&
      !/^\d+$/.test(item.pass_name) &&
      !/^\d+$/.test(item.pass_group_name)
  );

  let finalData = [];
  filteredSortedData.forEach((item) => {
    finalData.push({
      ...item,
      pass_group_name: `${item.pass_group_name} (Min)`,
      original_pass_name: item.pass_group_name,
      dataIndex: "min",
      key: "min",
    });
    finalData.push({
      ...item,
      pass_group_name: `${item.pass_group_name} (Max)`,
      original_pass_name: item.pass_group_name,
      dataIndex: "max",
      key: "max",
    });
  });

  return finalData;
};

// Helper to build pass_updates/zone_updates for a job's data
function buildJobRequestBody(jobData, selectedParams, statusCalcMethod) {
  if (!jobData) return null;
  const pass_updates = [];
  const passGroups = new Set();
  const { conversionFactors = {} } = jobData.limits || {};
  jobData.rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key !== "parameter") {
        passGroups.add(key);
      }
    });
  });
  passGroups.forEach((passGroup) => {
    const passUpdate = {
      pass_group_name: passGroup.replace(/ \(Min\)| \(Max\)$/, ""),
    };
    jobData.rows.forEach((row) => {
      const paramName = row.parameter
        .split("(")[0]
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");
      const isMin = passGroup.includes("(Min)");
      const value = row[passGroup];
      if (value !== undefined) {
        const conversionFactor = conversionFactors?.[paramName] || 1;
        // Only save a number if value is provided and valid, else save as null
        let finalValue = null;
        if (value !== null && value !== "" && !isNaN(Number(value))) {
          finalValue = Number(value) / conversionFactor;
        }
        passUpdate[`${paramName}_${isMin ? "min" : "max"}`] = finalValue;
      }
    });
    pass_updates.push(passUpdate);
  });
  const grouped = {};
  pass_updates.forEach((item) => {
    const name = item.pass_group_name;
    if (!grouped[name]) {
      grouped[name] = { pass_group_name: name };
    }
    Object.keys(item).forEach((key) => {
      if (key !== "pass_group_name") {
        grouped[name][key] = item[key];
      }
    });
  });
  let requestBody = {
    welding_system: jobData?.weldDetail?.welding_system,
    pipe_name: jobData?.weldDetail?.pipe_name,
    job_number: jobData?.weldDetail?.job_number,
    slope_in: jobData?.weldDetail?.slope_in_time || 0,
    slope_out: jobData?.weldDetail?.slope_out_time || 0,
    status_calculation_method: statusCalcMethod,
    status_calculation_level:
      jobData?.weldDetail?.statusCalculationLevel || "Pass Level",
    selected_parameter_list: selectedParams,
  };
  if (jobData?.weldDetail?.statusCalculationLevel === "Pass Level") {
    requestBody = { ...requestBody, pass_updates: Object.values(grouped) };
  } else {
    requestBody = { ...requestBody, zone_updates: Object.values(grouped) };
  }
  return requestBody;
}

const Data360LiveStatusConfiguration = () => {
  const {
    tableLimitData,
    weldDetails,
    selectedJobNumber,
    setSelectedJobNumber,
    selectedProject,
    precalculatedJobData,
  } = useStatusConfiguration();

  const reducerList = useSelector((state) => state);

  const canEdit = hasEditPermission(reducerList?.auth?.userDetails);
  const StatusData = useSelector(
    (state) => state.data360LiveStatusConfigReducer,
    (prev, next) => prev.loading === next.loading && prev.limits === next.limits
  );

  // --- NEW STATE: Retain edits for each job number ---
  const [pendingEdits, setPendingEdits] = useState({});
  const [isValid, setIsValid] = useState(true);
  const [hasMinMaxError, setHasMinMaxError] = useState(false);
  const [selectedParams, setSelectedParams] = useState({
    heat: false,
    volts: true,
    current: true,
    wire_speed: true,
    true_energy: false,
    travel_speed: true,
    oscillation_width: true,
  });
  const [statusCalcMethod, setStatusCalcMethod] = useState(
    StatusData?.limits?.projectData?.statusCalculationMethod
  );

  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  const handleErrorModalClose = () => {
    setErrorModalVisible(false);
  };

  // Check if at least one parameter is selected for the current job
  const hasSelectedParams = useMemo(() => {
    const currentJobEdit = pendingEdits[selectedJobNumber];
    if (currentJobEdit) {
      return (
        Object.values(currentJobEdit.selectedParams || {}).filter(Boolean)
          .length > 0
      );
    }
    return Object.values(selectedParams).filter(Boolean).length > 0;
  }, [pendingEdits, selectedJobNumber, selectedParams]);

  // Validate min/max values - only for checked rows
  const validateMinMaxValues = useCallback((jobData, selectedParams) => {
    if (!jobData || !jobData.rows || !jobData.columns)
      return { hasError: false, errorDetails: null };

    const passGroups = new Set();
    const allErrors = [];

    // Extract pass group names from columns (remove Min/Max suffixes)
    jobData.columns.forEach((col) => {
      const passGroupName = col.columnName.replace(/ \(Min\)| \(Max\)$/, "");
      passGroups.add(passGroupName);
    });

    // Check each pass group for min/max validation
    for (const passGroup of passGroups) {
      const minColumn = `${passGroup} (Min)`;
      const maxColumn = `${passGroup} (Max)`;

      for (const row of jobData.rows) {
        // Extract parameter key from row parameter name
        const paramKey = row.parameter
          .split("(")[0]
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_");

        // Only validate if this parameter is selected (checked)
        if (selectedParams && selectedParams[paramKey]) {
          const minValue = row[minColumn];
          const maxValue = row[maxColumn];

          // Check if values exist and are valid numbers
          const minExists =
            minValue !== null &&
            minValue !== undefined &&
            minValue !== "" &&
            !isNaN(Number(minValue));
          const maxExists =
            maxValue !== null &&
            maxValue !== undefined &&
            maxValue !== "" &&
            !isNaN(Number(maxValue));

          // If both are empty, no error (allow save)
          if (!minExists && !maxExists) {
            continue;
          }

          // If one has value and the other is empty, show error
          if (minExists !== maxExists) {
            allErrors.push({
              passGroup: passGroup,
              parameter: row.parameter.split("(")[0].trim(),
              parameterFull: row.parameter,
              minValue: minExists ? Number(minValue) : null,
              maxValue: maxExists ? Number(maxValue) : null,
              rowIndex: jobData.rows.indexOf(row),
              columnName: minExists ? minColumn : maxColumn,
              errorType: "missing_value",
            });
            continue;
          }

          // If both exist, validate min <= max
          if (minExists && maxExists) {
            const minNum = Number(minValue);
            const maxNum = Number(maxValue);

            if (minNum > maxNum || minNum === maxNum) {
              allErrors.push({
                passGroup: passGroup,
                parameter: row.parameter.split("(")[0].trim(),
                parameterFull: row.parameter,
                minValue: minNum,
                maxValue: maxNum,
                rowIndex: jobData.rows.indexOf(row),
                columnName: minColumn,
                errorType: "invalid_range",
              });
            }
          }
        }
      }
    }

    if (allErrors.length > 0) {
      return {
        hasError: true,
        errorDetails: {
          jobNumber: jobData?.weldDetail?.job_number || "Unknown",
          calculationLevel:
            jobData?.weldDetail?.statusCalculationLevel || "Pass Level",
          errors: allErrors,
          totalErrors: allErrors.length,
        },
      };
    }

    return { hasError: false, errorDetails: null };
  }, []);

  // --- Load job data from pendingEdits or precalculatedJobData ---
  const [currentJobData, setCurrentJobData] = useState(null);
  useEffect(() => {
    if (!selectedJobNumber) return;
    if (pendingEdits[selectedJobNumber]) {
      setCurrentJobData(pendingEdits[selectedJobNumber].jobData);
      setSelectedParams(pendingEdits[selectedJobNumber].selectedParams);
      setStatusCalcMethod(pendingEdits[selectedJobNumber].statusCalcMethod);
    } else if (precalculatedJobData[selectedJobNumber]) {
      setCurrentJobData(precalculatedJobData[selectedJobNumber]);
      // Set default params from weldDetails
      const [jobNum, pipeName] = selectedJobNumber.split(":");
      const selectedWeldDetail = weldDetails?.find(
        (ite) =>
          ite.job_number == jobNum &&
          (pipeName
            ? ite.pipe_name == pipeName
            : ite.pipe_name == null || ite.pipe_name == "null")
      );
      const defaultParams = selectedWeldDetail?.selected_parameter_list || {
        heat: false,
        volts: true,
        current: true,
        wire_speed: true,
        true_energy: false,
        travel_speed: true,
        oscillation_width: true,
      };
      setSelectedParams(defaultParams);
      setStatusCalcMethod(
        StatusData?.limits?.projectData?.statusCalculationMethod
      );

      // Validate the loaded data immediately
      const jobData = precalculatedJobData[selectedJobNumber];
      if (jobData) {
        const validationResult = validateMinMaxValues(jobData, defaultParams);
        setHasMinMaxError(validationResult.hasError);
        if (validationResult.hasError && validationResult.errorDetails) {
          setErrorDetails(validationResult.errorDetails);
        }
      }
    }
  }, [
    selectedJobNumber,
    pendingEdits,
    precalculatedJobData,
    weldDetails,
    StatusData?.limits?.projectData?.statusCalculationMethod,
    validateMinMaxValues,
  ]);

  // Validate current job data when it changes
  useEffect(() => {
    if (currentJobData) {
      const validationResult = validateMinMaxValues(
        currentJobData,
        selectedParams
      );
      setHasMinMaxError(validationResult.hasError);
      if (validationResult.hasError && validationResult.errorDetails) {
        setErrorDetails(validationResult.errorDetails);
      }
    } else {
      setHasMinMaxError(false);
      setErrorDetails(null);
    }
  }, [currentJobData, validateMinMaxValues, selectedParams]);

  // --- On cell value change, update pendingEdits ---
  const handleCellValueChange = useCallback(
    (params) => {
      const { colDef, newValue, data } = params;
      const { field } = colDef;
      if (newValue !== null && newValue !== undefined && newValue !== "") {
        const numValue = Number(newValue);
        if (isNaN(numValue)) {
          message.error("Please enter a valid number");
          setIsValid(false);
          return;
        }
      }
      setCurrentJobData((prevData) => {
        if (!prevData) return null;
        const updatedRows = prevData.rows.map((row) => {
          if (row.parameter === data.parameter) {
            const value = newValue === "" ? null : Number(newValue);
            return {
              ...row,
              [field]: value,
            };
          }
          return row;
        });
        const newData = {
          ...prevData,
          rows: updatedRows,
        };

        // Validate min/max values after update
        const validationResult = validateMinMaxValues(newData, selectedParams);
        setHasMinMaxError(validationResult.hasError);

        if (validationResult.hasError && validationResult.errorDetails) {
          setErrorDetails(validationResult.errorDetails);
          setErrorModalVisible(true);
          return prevData;
        }

        setPendingEdits((prev) => ({
          ...prev,
          [selectedJobNumber]: {
            ...(prev[selectedJobNumber] || {}),
            jobData: newData,
            selectedParams,
            statusCalcMethod,
          },
        }));
        return newData;
      });
    },
    [selectedJobNumber, selectedParams, statusCalcMethod, validateMinMaxValues]
  );

  // --- On selectedParams change, update pendingEdits ---
  const handleSelectedParamsChange = (params) => {
    setSelectedParams(params);
    setPendingEdits((prev) => ({
      ...prev,
      [selectedJobNumber]: {
        ...(prev[selectedJobNumber] || {}),
        jobData: currentJobData,
        selectedParams: params,
        statusCalcMethod,
      },
    }));

    // Check if user is trying to uncheck a parameter in the last row
    if (
      currentJobData &&
      currentJobData.rows &&
      currentJobData.rows.length > 0
    ) {
      const lastRowParameter =
        currentJobData.rows[currentJobData.rows.length - 1]?.parameter;
      if (lastRowParameter) {
        const lastRowParamKey = lastRowParameter
          .split("(")[0]
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_");

        // If trying to uncheck the last row parameter and it would be the only one unchecked
        const currentlySelected = Object.entries(selectedParams).filter(
          ([value]) => value === true
        );
        const wouldBeSelected = Object.entries(params).filter(
          ([value]) => value === true
        );

        // If we're unchecking the last row parameter and it's currently the only one selected
        if (
          currentlySelected.length === 1 &&
          currentlySelected[0][0] === lastRowParamKey &&
          wouldBeSelected.length === 0
        ) {
          message.warning(
            "Cannot uncheck the last row's parameter when it's the only one selected."
          );
          return; // Prevent the update
        }

        // If we're trying to uncheck the last row parameter and only one would remain
        if (
          wouldBeSelected.length === 1 &&
          selectedParams[lastRowParamKey] === true &&
          params[lastRowParamKey] === false
        ) {
          message.warning("Cannot uncheck the last row's parameter.");
          return; // Prevent the update
        }
      }
    }

    // If checks pass, update the state
    setSelectedParams(params);
    setPendingEdits((prev) => ({
      ...prev,
      [selectedJobNumber]: {
        ...(prev[selectedJobNumber] || {}),
        jobData: currentJobData,
        selectedParams: params,
        statusCalcMethod,
      },
    }));

    // Validate min/max values after parameter selection change
    if (currentJobData) {
      const validationResult = validateMinMaxValues(currentJobData, params);
      setHasMinMaxError(validationResult.hasError);
    }
  };

  // --- On statusCalcMethod change, update pendingEdits ---
  const handleStatusCalcMethodChange = (value) => {
    setStatusCalcMethod(value);
    setPendingEdits((prev) => ({
      ...prev,
      [selectedJobNumber]: {
        ...(prev[selectedJobNumber] || {}),
        jobData: currentJobData,
        selectedParams,
        statusCalcMethod: value,
      },
    }));
  };

  // --- On slope_in/out change, update pendingEdits ---
  const handleSlopeChange = (field, value) => {
    setCurrentJobData((prev) => {
      const newJobData = {
        ...prev,
        weldDetail: {
          ...prev?.weldDetail,
          [field]: value,
        },
      };
      setPendingEdits((prevEdits) => ({
        ...prevEdits,
        [selectedJobNumber]: {
          ...(prevEdits[selectedJobNumber] || {}),
          jobData: newJobData,
          selectedParams,
          statusCalcMethod,
        },
      }));
      return newJobData;
    });
  };

  // --- On Save: aggregate all pendingEdits into API payload ---
  const dispatch = useDispatch();
  const handleSave = async () => {
    try {
      // Check if at least one parameter is selected for the current job
      const currentJobEdit = pendingEdits[selectedJobNumber];
      if (currentJobEdit) {
        const selectedParamCount = Object.values(
          currentJobEdit.selectedParams || {}
        ).filter(Boolean).length;
        if (selectedParamCount === 0) {
          message.error(
            "At least one parameter must be selected before saving"
          );
          return;
        }
      }

      // Check for min/max validation errors before proceeding
      if (hasMinMaxError) {
        // Ensure error details are set before showing modal
        if (currentJobData) {
          const validationResult = validateMinMaxValues(
            currentJobData,
            selectedParams
          );
          if (validationResult.hasError && validationResult.errorDetails) {
            setErrorDetails(validationResult.errorDetails);
          }
        }
        setErrorModalVisible(true);
        return;
      }

      const payload = Object.values(pendingEdits)
        .map((edit) => {
          return buildJobRequestBody(
            edit.jobData,
            edit.selectedParams,
            edit.statusCalcMethod
          );
        })
        .filter(Boolean);
      if (!payload.length) {
        message.error("No job data to save");
        return;
      }
      dispatch(startStatusConfigLoader());
      dispatch(
        updateLimits({ project_id: selectedProject.project_id, payload })
      );
    } catch (error) {
      console.error("Error saving status configuration:", error);
      message.error("Error saving status configuration");
    }
  };

  const Loader = StatusData?.loading;
  const hasJobNumbers = useMemo(
    () => tableLimitData?.some((item) => item.job_number),
    [tableLimitData]
  );
  const uniqueJobNumbers = useMemo(
    () => getUniqueJobNumbers(tableLimitData, weldDetails),
    [tableLimitData, weldDetails]
  );

  useEffect(() => {
    setPendingEdits({});
    setHasMinMaxError(false);
    dispatch(resetStatusConfigState());
  }, [selectedProject.project_id]);

  return (
    <>
      {Loader ? (
        <div className="slogdata-loader">
          <CommonLoader />
        </div>
      ) : (
        <div className="status-cofig-table">
          {tableLimitData && tableLimitData.length > 0 && hasJobNumbers ? (
            <>
              <div className="status-table-limit">
                <span className="select-job-no">Select Job Number:</span>
                <Select
                  className="job-number-selector"
                  placeholder="Select job number"
                  style={{ width: 200 }}
                  value={selectedJobNumber}
                  onChange={setSelectedJobNumber}
                >
                  {uniqueJobNumbers.map((jobNumber) => (
                    <Option key={jobNumber.value} value={jobNumber.value}>
                      {jobNumber.label}
                    </Option>
                  ))}
                </Select>

                {currentJobData && (
                  <Data360LiveStatusConfigurationGrid
                    rows={currentJobData.rows}
                    columns={currentJobData.columns}
                    onCellValueChanged={handleCellValueChange}
                    selectedParams={selectedParams}
                    setSelectedParams={handleSelectedParamsChange}
                    onSelectedParamsChange={handleSelectedParamsChange}
                    jobNumber={currentJobData?.weldDetail?.job_number}
                  />
                )}
                <div className="input-select-flex">
                  <div className="slop-time">
                    <span className="slop-span">{"Slope Time (sec) : In"}</span>
                    <InputNumber
                      min={0}
                      max={10}
                      value={currentJobData?.weldDetail?.slope_in_time || 0}
                      className="slop-input"
                      disabled={false}
                      onChange={(value) =>
                        handleSlopeChange("slope_in_time", value)
                      }
                    />
                    <span className="slop-span"> Out </span>
                    <InputNumber
                      min={0}
                      max={10}
                      value={currentJobData?.weldDetail?.slope_out_time || 0}
                      className="slop-input"
                      disabled={false}
                      onChange={(value) =>
                        handleSlopeChange("slope_out_time", value)
                      }
                    />
                  </div>
                  <div className="slop-time">
                    <span className="slop-span">
                      Status Calculation Method:
                    </span>
                    <Select
                      defaultValue={
                        StatusData?.limits?.projectData?.statusCalculationMethod
                      }
                      value={statusCalcMethod}
                      onChange={handleStatusCalcMethodChange}
                      className="status-selector"
                      disabled={false}
                    >
                      <Option value="Instantaneous">Instantaneous</Option>
                      <Option value="AvgByPass">Average Pass</Option>
                      <Option value="AvgByZone">Average Zone</Option>
                      <Option value="AvgByTilt">Average Tilt</Option>
                    </Select>
                  </div>

                  <div className="slop-time">
                    <span className="slop-span">Calculation Level: </span>
                    <Select
                      value={currentJobData?.weldDetail?.statusCalculationLevel}
                      className="status-selector"
                      disabled={true}
                      onChange={(value) => {
                        setCurrentJobData({
                          ...currentJobData,
                          weldDetail: {
                            ...currentJobData?.weldDetail,
                            statusCalculationLevel: value,
                          },
                        });
                      }}
                    >
                      <Option value="Pass Level">Pass Level</Option>
                      <Option value="Zone Level">Zone Level</Option>
                    </Select>
                  </div>
                  {canEdit && (
                    <Button
                      type="primary"
                      className="status-submit-btn"
                      onClick={handleSave}
                      disabled={!isValid || !hasSelectedParams}
                    >
                      Save
                    </Button>
                  )}
                  {!hasSelectedParams && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#ff4d4f",
                        marginTop: "4px",
                      }}
                    >
                      At least one parameter must be selected
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="slog-NoData">
              <p>
                Status configuration details are not available for this project.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Error Popup */}
      <ErrorPopup
        visible={errorModalVisible}
        errorDetails={errorDetails}
        onClose={handleErrorModalClose}
      />
    </>
  );
};

export default Data360LiveStatusConfiguration;
